/* Copyright (c) 2020-present CloudIO Inc */

import {
  DBRow,
  IoOrganizations,
  IoUserPagesV,
} from '@cloudio-saas/datasource-types';
import { atom, emptyFunction, useStateXAtom } from '@cloudio-saas/statex';
import {
  Avatar,
  Box,
  Dialog,
  IconButton,
  InputBase,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { IconType } from 'componentregistry/icons/types';
import { Platform } from 'componentregistry/platform/Platform';
import {
  cancelPageLoad,
  onPageLoadEnd,
  startPageLoad,
} from 'components/Page/PageProgressed';
import IOSSwitch from 'custom/IOSSwitch';
import { MotionIconButton } from 'custom/Motion';
import { AnimatePresence, motion } from 'framer-motion';
import useLocalState from 'hooks/useLocalState';
import useWindowSize from 'hooks/useWindowSize';
import Icon from 'icons/Icon';
import { usePending, usePlatform } from 'platform/PlatformHooks';
import React, { startTransition, useEffect, useMemo, useState } from 'react';
import { useUserPages, useUserPagesStore } from 'store/utils/utils';
import WaveDots from 'utils/WaveDots';
import NoDataFound from '../../api/NoDataFound';
import { useStoreBusy, useStoreCurrentRecord } from '../../store/useAppStore';
import { Masonry } from '@mui/lab';
import useDataStore from 'store/useDataStore';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: 0,
    boxSizing: 'border-box',
    '& *': {
      boxSizing: 'border-box',
    },
  },
  inner: {
    height: '100%',
    maxWidth: 1200,
    position: 'relative',
    flex: 1,
    margin: '0 40px',
  },
  icon: {
    marginRight: 12,
  },
  cell: {
    flexBasis: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  search: {
    padding: theme.spacing(1),
    flex: 1,
    fontSize: '1.5em',
  },
  body: {
    position: 'absolute',
    top: 90,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  searchWrapper: {
    zIndex: 2,
    position: 'sticky',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    padding: '20px 0',
    height: 90,
  },
  iconButton: {
    padding: 10,
  },
  close: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 3,
  },
  block: {
    display: 'flex',
    flexBasis: '100%',
    maxWidth: '100%',
    flexGrow: 0,
  },
  row: {
    display: 'flex',
  },
  count: {
    color: theme.palette.text.secondary,
  },
  role: {
    minWidth: 300,
    overflow: 'hidden',
  },
  link: {
    border: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    minHeight: 40,
    color: theme.palette.primary.main,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexDirection: 'row',
    '&:hover': {
      backgroundColor: theme.palette.divider,
      borderRadius: '8px',
    },
  },
  active: {
    backgroundColor: theme.palette.divider,
    borderRadius: '8px',
  },
  avatar: {
    backgroundColor: theme.palette.divider,
  },
  marginLeft: {
    marginLeft: '16px',
  },
}));

// const sleep = async (ms: number) =>
//   new Promise<void>((resolve) => {
//     setTimeout(() => {
//       resolve();
//     }, ms);
//   });

const MotionListItem = motion(ListItem);
const MotionTypography = motion(Typography);
const resolvedPromise = new Promise<void>((resolve) => resolve());

const Entry = React.memo(
  ({
    onClose,
    page,
    platform,
    width,
    appUid,
    pageUid,
  }: {
    onClose: () => void;
    page: IoUserPagesV;
    platform: Platform;
    width: number;
    appUid: string;
    pageUid: string;
  }) => {
    const classes = useStyles();
    const [pending, setPending] = usePending();
    const orgStore = useDataStore<IoOrganizations>({
      appUid: 'cloudio',
      pageId: '$$Sidebar$$',
      ds: 'IoOrganizations',
      alias: 'IoOrgsLogo',
    });
    orgStore.query();
    const { record: orgRecord } = useStoreCurrentRecord(orgStore);
    const active =
      platform.pageId === page.pageUid &&
      platform.appUid === page.appUid &&
      platform.roleUid === page.roleUid;
    return (
      <MotionListItem
        layoutId={`${page.pageUid}-${page.roleUid}-${page.appUid}`}
        className={clsx({ [classes.link]: true, [classes.active]: active })}
        onClick={async () => {
          if (pending) return;
          if (active) {
            onClose();
            return;
          }
          const paramsStr = `role=${page.roleUid}`;
          platform.startAsyncTransition(async () => {
            let unsub = emptyFunction;

            const promise =
              page.appUid === appUid && page.pageUid === pageUid
                ? resolvedPromise
                : new Promise<void>((resolve) => {
                    let timeout: NodeJS.Timeout | null = null;
                    startPageLoad(
                      page.appUid,
                      page.pageUid,
                      `gotoPage${page.pageUid}?${paramsStr}`,
                    );
                    // console.log(
                    //   'GoToPage appProgressComplete waiting...',
                    //   _appUid,
                    //   _page,
                    // );
                    unsub = onPageLoadEnd(page.appUid, page.pageUid, () => {
                      if (timeout) {
                        clearTimeout(timeout);
                      }
                      // console.log('GoToPage appProgressComplete done...', _appUid, _page);
                      unsub();
                      onClose();
                      resolve();
                    });

                    timeout = setTimeout(() => {
                      // console.log(
                      //   'GoToPage appProgressComplete timeout!',
                      //   page.appUid,
                      //   page.pageUid,
                      // );
                      // taking more than 6s. show the page anyway
                      unsub();
                      cancelPageLoad(
                        page.appUid,
                        page.pageUid,
                        `gotoPage${page.pageUid}?${paramsStr}`,
                      );
                      onClose();
                      resolve();
                    }, 6000);
                  });

            if (!platform.goToPage(page.appUid, page.pageUid, paramsStr)) {
              if (promise !== resolvedPromise) {
                // console.warn('GoToPage appProgressComplete cancelPageLoad', _appUid, _page);
                cancelPageLoad(
                  page.appUid,
                  page.pageUid,
                  `gotoPage${page.pageUid}?${paramsStr}`,
                );
              }
            }
            if (promise === resolvedPromise) {
              onClose();
            }
            return promise;
          }, setPending);
        }}
        sx={{ maxWidth: width }}
      >
        <ListItemAvatar>
          <Avatar className={classes.avatar}>
            {pending ? (
              <WaveDots active />
            ) : (
              <Icon
                width={18}
                color="primary"
                height={18}
                iconName={(page.pageIcon ?? 'desktop').split(':')[0]}
                iconType={
                  ((page.pageIcon ?? 'desktop').split(':')[1] as IconType) ??
                  'light'
                }
              />
            )}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography overflow="hidden" textOverflow="ellipsis">
              {page.pageName}
            </Typography>
          }
          secondary={
            orgRecord?.publicMd?.navAppCode ? null : (
              <Typography
                overflow="hidden"
                textOverflow="ellipsis"
                variant="body2"
                color="text.secondary"
              >
                {page.pageUid}
              </Typography>
            )
          }
        />
      </MotionListItem>
    );
  },
);

const Content = React.memo(
  ({
    onClose,
    data,
    appUid,
    pageUid,
  }: {
    onClose: () => void;
    data: {
      totalCount: number;
      filterCount: number;
      headers: {
        appRole: string;
        title: string;
        pages: IoUserPagesV[];
      }[];
    };
    appUid: string;
    pageUid: string;
  }) => {
    const platform = usePlatform();
    const classes = useStyles();
    const [ww] = useWindowSize();

    if (data.filterCount === 0) {
      return <NoDataFound msg="No pages found!" />;
    }

    const cols = Math.floor(Math.min(1200, ww - 80) / 300);
    const colWidth = Math.floor(Math.min(1200, ww - 80) / cols);

    return (
      <div className={classes.body}>
        <AnimatePresence>
          <Masonry columns={cols} spacing={2} sx={{ overflowX: 'hidden' }}>
            {data.headers.map((item) => (
              <Box className={classes.role} key={item.appRole}>
                <MotionTypography
                  layoutId={item.title}
                  className={classes.title}
                  variant="subtitle1"
                  sx={{ margin: '0 16px' }}
                >
                  {item.title}
                </MotionTypography>
                <Box
                  component="ul"
                  sx={{ margin: 0, padding: 0, paddingTop: 1 }}
                >
                  {item.pages.map((page) => (
                    <Entry
                      key={`${page.pageUid}-${page.roleUid}-${page.appUid}`}
                      onClose={onClose}
                      platform={platform}
                      page={page}
                      width={colWidth}
                      appUid={appUid}
                      pageUid={pageUid}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Masonry>
        </AnimatePresence>
      </div>
    );
  },
);

const filterAtom = atom({
  path: ['transient', 'pageMenuFilter'],
  defaultValue: '',
});

const allAppsAtom = atom({
  path: ['transient', 'allAppsFilter'],
  defaultValue: false,
});

function compare(a: IoUserPagesV, b: IoUserPagesV) {
  let comparison = 0;
  if (a.appName > b.appName) {
    comparison = 1;
  } else if (a.appName < b.appName) {
    comparison = -1;
  } else if (a.roleName > b.roleName) {
    comparison = 1;
  } else if (a.roleName < b.roleName) {
    comparison = -1;
  } else if (a.pageName > b.pageName) {
    comparison = 1;
  } else if (a.pageName < b.pageName) {
    comparison = -1;
  }
  return comparison;
}

interface Data {
  totalCount: number;
  filterCount: number;
  headers: {
    appRole: string;
    title: string;
    pages: IoUserPagesV[];
  }[];
}

function makeData(records: DBRow<IoUserPagesV>[], filterLC: string): Data {
  records = records.filter((r) => r.showInNavigationMenu === 'Y').sort(compare);

  const _data = records.reduce((map, row) => {
    let val = map[`${row.appUid}:${row.roleUid}`];
    if (!val) {
      val = {
        app: row.appName,
        role: row.roleName,
        pages: [row],
      };
      map[`${row.appUid}:${row.roleUid}`] = val;
    } else {
      val.pages.push(row);
    }
    return map;
  }, {} as Record<string, { app: string; role: string; pages: IoUserPagesV[] }>);

  let totalCount = 0;
  let filterCount = 0;

  const headers: {
    appRole: string;
    title: string;
    pages: IoUserPagesV[];
  }[] = [];
  Object.keys(_data).forEach((appRole) => {
    const row = _data[appRole];
    totalCount += row.pages.length;
    const pages = row.pages.filter(
      (page) =>
        !filterLC ||
        page.pageName.toLowerCase().includes(filterLC) ||
        page.pageUid.toLowerCase().includes(filterLC),
    );
    if (pages.length) {
      filterCount += pages.length;
      headers.push({
        title: `${row.app} - ${row.role}`,
        appRole,
        pages,
      });
    }
  });
  return { totalCount, filterCount, headers };
}

export default function PageMenu({
  onClose,
  layoutId,
  appUid,
  pageUid,
  platform,
}: {
  onClose: () => void;
  layoutId: string;
  appUid: string;
  pageUid: string;
  platform: Platform;
}) {
  const classes = useStyles();
  const [f, setFilter] = useStateXAtom(filterAtom);
  const [allApps, setAllApps] = useStateXAtom(allAppsAtom);
  const [localFilter, setLocalFilter] = useLocalState(f, (val) => {
    setFilter(val);
  });
  const [w] = useWindowSize();
  const width = Math.min(1200, w - 180);
  let pages = useUserPages();
  const store = useUserPagesStore();
  const busy = useStoreBusy(store);
  pages = useMemo(
    () => (allApps ? pages : pages.filter((r) => r.appUid === appUid)),
    [allApps, appUid, pages],
  );
  const [data, setData] = useState<Data>(() => ({
    filterCount: 0,
    totalCount: 0,
    headers: [],
  }));

  useEffect(() => {
    if (pages.length) {
      startTransition(() => {
        setData(makeData(pages, f.toLowerCase()));
      });
    }
  }, [f, pages, appUid]);

  const ref = React.useRef<HTMLInputElement>(null);
  return (
    <Dialog
      onClick={() => {
        ref.current?.focus();
      }}
      fullScreen
      open
      onClose={onClose}
    >
      <div className={classes.root}>
        <div className={classes.inner}>
          <div className={classes.searchWrapper} style={{ width, height: 90 }}>
            <Typography
              sx={{ display: { sm: 'block', xs: 'none' } }}
              className={classes.count}
              variant="h5"
            >
              Navigation
            </Typography>
            <IconButton className={classes.iconButton} aria-label="search">
              <Icon
                width={20}
                height={20}
                iconType="light"
                iconName="search"
                color="ioGrey"
              />
            </IconButton>
            <InputBase
              data-testid="page-nav-filter"
              inputRef={ref}
              className={classes.search}
              placeholder="Search..."
              value={localFilter ?? ''}
              onChange={(e) => {
                setLocalFilter(e.target.value);
              }}
              autoFocus
            />
            <Typography className={classes.count} variant="subtitle2">
              {`${
                data.filterCount === data.totalCount
                  ? data.totalCount
                  : `${data.filterCount} / ${data.totalCount}`
              } pages`}
            </Typography>
            <IOSSwitch
              checked={allApps}
              onChange={() => {
                setAllApps(!allApps);
              }}
              checkedLabel="All Apps"
              unCheckedLabel="All Apps"
              className={classes.marginLeft}
              data-tip={
                allApps
                  ? 'Showing pages from all apps'
                  : 'Showing pages from current app'
              }
            />
          </div>
          {busy ? (
            <WaveDots active />
          ) : (
            <Content
              onClose={onClose}
              data={data}
              appUid={appUid}
              pageUid={pageUid}
            />
          )}
        </div>
      </div>
      <MotionIconButton
        data-testid="page-nav-close"
        layoutId={layoutId}
        onClick={onClose}
        className={classes.close}
        aria-label="close"
        size="medium"
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          Close
        </span>
        <Icon iconButton iconType="light" iconName="times" />
      </MotionIconButton>
    </Dialog>
  );
}
